import React, { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
    useTheme,
    CircularProgress,
    Typography,
    Card,
    Box,
    Grid,
    TextField,
    InputAdornment,
} from '@mui/material'
import { AssignmentLate, Search } from '@mui/icons-material'
import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { useDamages } from '../../hooks/useDamages'
import { DamageRow } from '../../components/Damage/DamageRow'
import { RootState } from '../../store'
import { Damage } from '../../types/lease'

export const DamageList = () => {
    const theme = useTheme()
    const { isLoading, damages, getDamages } = useDamages()
    const selectedLease = useSelector((state: RootState) => state.lease.selectedLease)
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        if (selectedLease?.id) {
            getDamages()
        }
    }, [selectedLease?.id, getDamages])

    const filteredDamages = useMemo(() => {
        if (!searchQuery.trim()) {
            return damages
        }
        const query = searchQuery.toLowerCase()
        return damages.filter((damage: Damage) => {
            const description = damage.damage_config?.description?.toLowerCase() || ''
            const areaName = damage.area_config_name?.toLowerCase() || ''
            const areaLabel = damage.area_label?.toLowerCase() || ''
            return (
                description.includes(query) ||
                areaName.includes(query) ||
                areaLabel.includes(query)
            )
        })
    }, [damages, searchQuery])

    const totalCost = useMemo(() => {
        return filteredDamages.reduce((total, damage) => {
            if (damage.damage_transactions.length === 0) {
                return total
            }
            const tenantCost = (damage.cost * damage.amount) / damage.damage_transactions.length
            return total + tenantCost
        }, 0)
    }, [filteredDamages])

    return (
        <ResponsivePageContainer
            title="Damages"
            subtitle="View your damage charges and details here"
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                }}
            >
                <Grid container spacing={3} sx={{ maxWidth: '800px' }}>
                    {!isLoading && damages.length > 0 && (
                        <>
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    placeholder="Search damages..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'rgba(138, 142, 140, 0.16)',
                                            color: 'rgba(255,255,255,0.8)',
                                            '& fieldset': {
                                                borderColor: 'rgba(255,255,255,0.1)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'rgba(255,255,255,0.2)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: 'rgba(255,255,255,0.5)',
                                            opacity: 1,
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card
                                    sx={{
                                        bgcolor: 'rgba(138, 142, 140, 0.16)',
                                        borderRadius: 2,
                                        boxShadow: 'none',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(255,255,255,0.5)',
                                            mb: 0.5,
                                        }}
                                    >
                                        Total Cost
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: 'rgba(255,255,255,0.9)',
                                            fontWeight: 600,
                                        }}
                                    >
                                        ${totalCost.toFixed(2)}
                                    </Typography>
                                </Card>
                            </Grid>
                        </>
                    )}

                    {isLoading ? (
                        <Grid item xs={12}>
                            <FlexDiv
                                style={{
                                    justifyContent: 'center',
                                    padding: theme.spacing(4),
                                }}
                            >
                                <CircularProgress
                                    size={40}
                                    sx={{ color: theme.palette.primary.main }}
                                />
                            </FlexDiv>
                        </Grid>
                    ) : filteredDamages.length === 0 ? (
                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    bgcolor: 'rgba(138, 142, 140, 0.16)',
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    p: 3,
                                    textAlign: 'center',
                                }}
                            >
                                <AssignmentLate
                                    sx={{
                                        fontSize: '48px',
                                        color: 'rgba(255,255,255,0.5)',
                                        mb: 2,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'rgba(255,255,255,0.8)',
                                        mb: 1,
                                    }}
                                >
                                    {searchQuery ? 'No Results Found' : 'No Damages Found'}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'rgba(255,255,255,0.5)',
                                    }}
                                >
                                    {searchQuery
                                        ? 'Try adjusting your search terms.'
                                        : "You don't have any damage charges at the moment."}
                                </Typography>
                            </Card>
                        </Grid>
                    ) : (
                        filteredDamages.map((damage: Damage) => (
                            <Grid item xs={12} key={damage.id}>
                                <DamageRow damage={damage} />
                            </Grid>
                        ))
                    )}
                </Grid>
            </Box>
        </ResponsivePageContainer>
    )
}